import React, { useEffect, useRef, useState } from "react";
import "./ChatAnimation.css";

interface ChatAnimationProps {
  onComplete?: () => void;
  token?: number;
  loop?: boolean;
  botSpeed?: number; // 1 = normal, 2 = 2x mais rápido
}

const ChatAnimationAlt: React.FC<ChatAnimationProps> = ({
  onComplete,
  token,
  loop = false,
  botSpeed = 1,
}) => {
  // ===== speed control =====
  const speedRef = useRef(botSpeed);
  useEffect(() => { speedRef.current = botSpeed; }, [botSpeed]);
  const scale = (ms: number) => Math.max(1, ms / (speedRef.current || 1));

  // ===== refs =====
  const chatRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const sendBtnRef = useRef<HTMLButtonElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const countRef = useRef<HTMLSpanElement>(null);

  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);

  // Atualizar ref quando estado muda
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // ===== lifecycle / abort =====
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const abortRef = useRef<AbortController | null>(null);
  const makeAbort = () => { abortRef.current?.abort(); abortRef.current = new AbortController(); };
  const isAborted = () => !!abortRef.current?.signal.aborted;

  const completedRef = useRef(false);
  const completeOnce = () => {
    if (completedRef.current && !loop) return;
    completedRef.current = true;
    if (mountedRef.current && !isAborted()) {
      if (!loop) {
        window.dispatchEvent(new CustomEvent("featureSlideComplete", { detail: { id: "chat-alt" } }));
        onComplete?.();
      } else {
        setTimeout(() => {
          if (!mountedRef.current || isAborted()) return;
          completedRef.current = false;
          setIsRunning(false);
          run();
        }, 3000);
      }
    }
  };

  // ===== utils =====
  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      if (isAborted()) return resolve();

      const checkPause = () => {
        if (isPausedRef.current) {
        //   console.log('Animação Alt pausada, aguardando... isPaused:', isPausedRef.current);
          setTimeout(checkPause, 3000);
        } else {
        //   console.log('Animação Alt retomada, continuando...');
          const id = setTimeout(() => { if (!isAborted()) resolve(); }, ms);
          abortRef.current?.signal.addEventListener("abort", () => { clearTimeout(id); resolve(); }, { once: true });
        }
      };

      checkPause();
    });

  const scrollBottom = () => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; };

  function ensureRowTopVisible(row: HTMLElement | null) {
    if (!row || !chatRef.current) return;
    const top = row.offsetTop - 12;
    chatRef.current.scrollTo({ top, behavior: "instant" as ScrollBehavior });
  }

  const icon = (p: string) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">${p}</svg>`;
  const TB = {
    copy: icon('<rect x="9" y="9" width="10" height="10" rx="2"/><rect x="5" y="5" width="10" height="10" rx="2"/>'),
    edit: icon('<path d="M4 21h4l11-11-4-4L4 17v4z"/>'),
    open: icon('<path d="M14 3h7v7"/><path d="M21 3l-9 9"/><rect x="3" y="7" width="11" height="14" rx="2"/>'),
    refresh: icon('<path d="M4.5 4.5v6h6"/><path d="M19.5 19.5v-6h-6"/><path d="M20 9A8 8 0 0 0 6 6M18 18a8 8 0 0 1-14-3"/>'),
  };
  const CHART = icon('<path d="M3 3v18h18"/><path d="M7 15v-5"/><path d="M12 15V7"/><path d="M17 15v-8"/>');

  const botAvatarSVG = () =>
    icon(`
      <path d="M12 3v2"/>
      <rect x="6" y="7" width="12" height="9" rx="2"/>
      <path d="M6 11H4M20 11h-2"/>
      <circle cx="10" cy="11" r="1.2"/><circle cx="14" cy="11" r="1.2"/>
      <path d="M9 16v1a3 3 0 0 0 6 0v-1"/>
    `);

  function makeMsg(role: "user" | "bot") {
    if (isAborted()) return { row: null as any, bubble: document.createElement("div") };

    const row = document.createElement("div");
    row.className = `msg ${role}`;

    const avatar = document.createElement("div");
    avatar.className = "avatar";
    if (role === "bot") avatar.innerHTML = botAvatarSVG();
    else { avatar.classList.add("user"); avatar.innerHTML = icon('<circle cx="12" cy="8" r="4"/><path d="M4 20c1.5-3.5 5-5 8-5s6.5 1.5 8 5"/>'); }

    const bubble = document.createElement("div");
    bubble.className = "bubble";

    if (role === "bot") {
      bubble.innerHTML = `<span class="label">${CHART} Insights</span>`;
      const tb = document.createElement("div");
      tb.className = "toolbar";
      tb.innerHTML = `
        <div class="tb" title="Repetir">${TB.refresh}</div>
        <div class="tb" title="Abrir">${TB.open}</div>
        <div class="tb" title="Editar">${TB.edit}</div>
        <div class="tb" title="Copiar">${TB.copy}</div>`;
      bubble.appendChild(tb);
    }

    if (role === "bot") { row.appendChild(avatar); row.appendChild(bubble); }
    else { row.appendChild(bubble); row.appendChild(avatar); }

    chatRef.current?.appendChild(row);
    scrollBottom();
    return { row, bubble };
  }

  const center = (el: HTMLElement | null) => {
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  const moveCursorTo = (el: HTMLElement | null, ms = 650) =>
    new Promise<void>((resolve) => {
      if (!el || !cursorRef.current || isAborted()) return resolve();
      const { x, y } = center(el);
      cursorRef.current.style.transition = `transform ${ms}ms cubic-bezier(.2,.8,.2,1)`;
      cursorRef.current.style.transform = `translate(${x - 11}px,${y - 11}px)`;
      const id = setTimeout(resolve, ms + 40);
      abortRef.current?.signal.addEventListener("abort", () => { clearTimeout(id); resolve(); }, { once: true });
    });

  async function clickEl(el: HTMLElement | null) {
    if (!el || isAborted()) return;
    el.dispatchEvent(new MouseEvent("pointerdown", { bubbles: true }));
    el.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    (el as HTMLButtonElement).click?.();
    el.dispatchEvent(new MouseEvent("mouseup", { bubbles: true }));
    el.dispatchEvent(new MouseEvent("pointerup", { bubbles: true }));
    await wait(scale(200));
  }

  function addTime(t: string) {
    if (isAborted()) return;
    const el = document.createElement("div");
    el.className = "time";
    el.textContent = t;
    chatRef.current?.appendChild(el);
  }

  const wrapMd = (html: string) => `<div class="md">${html}</div>`;

  // ===== streaming HTML =====
  function shallowClone<T extends HTMLElement>(el: T): T {
    const clone = document.createElement(el.tagName.toLowerCase()) as T;
    clone.className = el.className;
    for (const a of Array.from(el.attributes)) if (a.name !== "class") clone.setAttribute(a.name, a.value);
    return clone;
  }
  const appendAndScroll = (parent: HTMLElement, child: Node) => { parent.appendChild(child); scrollBottom(); };

  async function showBotStreamHtml(html: string, opts: { delay?: number; headerDelay?: number } = {}) {
    const baseDelay = opts.delay ?? 35;
    const baseHeader = opts.headerDelay ?? 180;
    const charDelay = Math.min(50, scale(baseDelay));
    const headerPause = scale(baseHeader);

    if (isAborted()) return;

    const { row, bubble } = makeMsg("bot");
    ensureRowTopVisible(row);

    const content = document.createElement("div");
    content.style.marginTop = "8px";
    if (!/class\s*=\s*["']?md["']?/i.test(html)) content.className = "md";
    bubble.appendChild(content);

    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    const revealChildren = async (src: Node, dst: HTMLElement) => {
      const children = Array.from(src.childNodes);
      for (const c of children) {
        await revealNode(c, dst);
        if (isAborted()) return;
      }
    };

    const revealNode = async (node: Node, host: HTMLElement) => {
      if (isAborted()) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const span = document.createElement("span");
        host.appendChild(span);
        const txt = node.textContent || "";
        for (const ch of txt) {
          if (isAborted()) return;
          span.textContent += ch;
          scrollBottom();
          await wait(charDelay);
        }
        return;
      }

      if (!(node instanceof HTMLElement)) {
        appendAndScroll(host, node.cloneNode(true));
        return;
      }

      const tag = node.tagName.toLowerCase();

      if (/^h[1-6]$/.test(tag)) {
        const h = shallowClone(node);
        appendAndScroll(host, h);
        await revealChildren(node, h);
        await wait(headerPause);
        return;
      }

      if (tag === "p" || tag === "blockquote") {
        const p = shallowClone(node);
        appendAndScroll(host, p);
        await revealChildren(node, p);
        await wait(scale(100));
        return;
      }

      if (tag === "ul" || tag === "ol") {
        const list = shallowClone(node) as HTMLUListElement | HTMLOListElement;
        appendAndScroll(host, list);
        const items = Array.from(node.children) as HTMLElement[];
        for (const li of items) {
          const liClone = shallowClone(li);
          list.appendChild(liClone);
          await revealChildren(li, liClone);
          await wait(scale(80));
        }
        return;
      }

      if (tag === "table") {
        const table = shallowClone(node) as HTMLTableElement;
        table.innerHTML = "";
        appendAndScroll(host, table);
        const thead = node.querySelector("thead");
        const tbody = node.querySelector("tbody");
        if (thead) {
          table.appendChild(thead.cloneNode(true));
          await wait(scale(120));
        }
        const newTbody = document.createElement("tbody");
        table.appendChild(newTbody);
        if (tbody) {
          const rows = Array.from(tbody.querySelectorAll("tr"));
          for (const tr of rows) {
            newTbody.appendChild(tr.cloneNode(true));
            scrollBottom();
            await wait(scale(70));
          }
        }
        return;
      }

      if (tag === "hr") {
        host.appendChild(node.cloneNode(true));
        await wait(scale(80));
        return;
      }

      const holder = shallowClone(node);
      appendAndScroll(host, holder);
      await revealChildren(node, holder);
    };

    await revealChildren(tmp, content);
  }

  // ===== user typing =====
  async function showUser(text: string) {
    if (isAborted()) return;

    const composerNow = composerRef.current;
    await moveCursorTo(composerNow, 650);

    const composer = composerRef.current;
    if (!composer || isAborted()) return;
    composer.value = "";
    composer.dispatchEvent(new Event("input"));
    for (const ch of text) {
      if (isAborted()) return;
      composer.value += ch;
      composer.dispatchEvent(new Event("input"));
      scrollBottom();
      await wait(18);
    }

    await moveCursorTo(sendBtnRef.current, 520);
    await clickEl(sendBtnRef.current);

    const { bubble } = makeMsg("user");
    const div = document.createElement("div");
    div.style.whiteSpace = "pre-wrap";
    bubble.appendChild(div);
    div.textContent = text;
  }

  // ===== conteúdo (HTML puro, sem **markdown**) =====
  const greetUser = "Olá Crafteer";

  const greetBot = wrapMd(`
<p>Olá! 👋 Sou especialista em análise de dados de seguros e estou pronto para ajudar
com qualquer pedido relacionado com clientes, apólices, recibos, simulações,
pesquisas em documentos ou exportações de dados.</p>

<p>Por favor, indique o que pretende analisar, pesquisar, simular ou exportar (por
exemplo: nome de cliente, tipo de seguro, relatório, simulação, exportação em
excel, etc.). Se precisar de uma simulação ou pesquisa específica, basta indicar –
trato de tudo automaticamente!</p>
  `);

  const qCaso = "O cliente Martim Edgar Branco teve um sinistro automóvel. Bateram-lhe por trás mas ele ia sob a influência de álcool. O que faço?";

  const botCaso = wrapMd(`
<p>O caso apresentado – sinistro automóvel em que o cliente Martim Edgar Branco foi
abalroado por trás, mas conduzia sob influência de álcool – está expressamente
previsto nas condições gerais dos seguros automóvel.</p>

<p><strong>O que dizem as cláusulas dos documentos oficiais (exemplo: Fidelidade_Auto_Liber3G_CG):</strong></p>

<ul>
  <li>
    <p><strong>Cláusula 5ª – Exclusões:</strong></p>
    <ul>
      <li>Ficam excluídos da cobertura “os danos causados ao veículo seguro quando
      o condutor conduza com uma taxa de alcoolemia superior à legalmente
      admitida ou acuse consumo de estupefacientes ou de outras drogas ou
      produtos tóxicos, ou esteja em estado de demência, ainda que acidental.” <a href="#" class="link">(Ver documento)</a></li>
    </ul>
  </li>
  <li>
    <p><strong>Cláusula 12ª – Direito de Regresso:</strong></p>
    <ul>
      <li>Mesmo que a seguradora pague indemnização a terceiros, pode exercer
      direito de regresso contra o condutor/tomador se este conduzia sob efeito
      de álcool.</li>
    </ul>
  </li>
</ul>

<p><strong>O que fazer:</strong></p>
<ol>
  <li><strong>Comunicar o sinistro à seguradora</strong> (dever de participação).</li>
  <li><strong>Informar o cliente</strong> que, por ter conduzido sob influência de álcool, os danos
      próprios (no seu veículo) não serão cobertos.</li>
  <li><strong>Se houver danos a terceiros</strong>, a seguradora poderá pagar a indemnização, mas
      tem direito de regresso – pode exigir ao cliente o reembolso desses valores.</li>
  <li><strong>Recolher toda a documentação</strong> (auto da polícia, testes de alcoolemia, relato do acidente).</li>
</ol>

<p><strong>Resumo:</strong><br/>
A condução sob influência de álcool é uma exclusão clara da cobertura, tanto para
danos próprios como para responsabilidade civil. Mesmo que o cliente não tenha
culpa no acidente, a existência de álcool no sangue impede a cobertura do seguro
para o condutor e pode originar direito de regresso por parte da seguradora.</p>

<p>Se precisa de apoio para comunicação ao cliente ou à seguradora, posso ajudar a
preparar o texto ou criar um email automático. Se quiser consultar a cláusula
completa, posso abrir o documento oficial.</p>
  `);

  const qEmailCliente = "Prepara um e-mail ao cliente";

  const botEmailCliente = wrapMd(`
<p>✅ O email ao cliente Martim Edgar de Branco está a ser preparado.</p>

<ul>
  <li><strong>Destinatário:</strong> <a href="mailto:cblock@example.com" class="link">cblock@example.com</a></li>
  <li><strong>Assunto:</strong> Informação sobre o seu sinistro automóvel</li>
  <li><strong>ID do Worker:</strong> 55</li>
</ul>

<p>O sistema está a gerar o conteúdo do email automaticamente, com a seguinte mensagem:</p>

<blockquote>
  Exmo. Sr. Martim Edgar de Branco,<br/><br/>
  Na sequência da participação do seu sinistro automóvel, informamos que, de acordo com as condições gerais do seguro automóvel, a condução sob influência de álcool constitui uma exclusão da cobertura. Assim, os danos próprios no seu veículo não serão cobertos pela apólice.<br/><br/>
  Caso existam danos a terceiros, a seguradora poderá indemnizar os lesados, mas poderá exercer o direito de regresso, exigindo-lhe o reembolso dos valores pagos.<br/><br/>
  Recomendamos que reúna toda a documentação relevante (auto da polícia, teste de alcoolemia, relato do acidente) e entre em contacto connosco para qualquer esclarecimento adicional.<br/><br/>
  Estamos disponíveis para apoiar em todo o processo.<br/><br/>
  Com os melhores cumprimentos,<br/>
  Carlos Oliveira<br/>
  Consultor Seguros
</blockquote>

<p>Assim que o email estiver pronto para revisão ou envio, será notificado. Se desejar
editar ou acrescentar algum detalhe, indique por favor!</p>

<div class="card">
  <div class="head">
    <div class="meta">
      <span>${icon('<rect x="3" y="4" width="18" height="14" rx="2"/><path d="M22 6l-10 7L2 6"/>')}</span>
      <div><strong>Informação sobre o seu sinistro automóvel</strong><br/><small>Email • #55</small></div>
    </div>
    <span class="pill pill--warn">Pronto para revisão</span>
    <div class="open" title="Abrir">${icon('<path d="M9 18l6-6-6-6"/>')}</div>
  </div>
</div>
  `);

  const qEmailSeg = "Prepara um e-mail à seguradora a comunicar o sinistro";

  const botEmailSeg = wrapMd(`
<p>O email para comunicar o sinistro à seguradora está a ser preparado automaticamente:</p>

<ul>
  <li><strong>Destinatário:</strong> <a class="link" href="mailto:sinistros@seguradora.pt">sinistros@seguradora.pt</a></li>
  <li><strong>Assunto:</strong> Participação de Sinistro – Martim Edgar de Branco</li>
  <li><strong>ID do Worker:</strong> 56</li>
</ul>

<p><strong>Resumo do conteúdo:</strong></p>

<blockquote>
  Venho por este meio comunicar a ocorrência de um sinistro automóvel
  envolvendo o cliente Martim Edgar de Branco (NIF: 642276455). O veículo foi
  abalroado por trás enquanto o condutor se encontrava sob influência de álcool.
  Toda a documentação relevante será disponibilizada. Solicita-se orientação
  sobre os procedimentos a adotar.
</blockquote>

<p>Assim que o email estiver pronto para revisão ou envio, será notificado. Se desejar
editar ou acrescentar algum detalhe, por favor indique!</p>

<div class="card">
  <div class="head">
    <div class="meta">
      <span>${icon('<rect x="3" y="4" width="18" height="14" rx="2"/>')}</span>
      <div><strong>Participação de sinistro – Martim Edgar de Branco</strong><br/><small>Email • #56</small></div>
    </div>
    <span class="pill pill--warn">Pronto para revisão</span>
    <div class="open" title="Abrir">${icon('<path d="M9 18l6-6-6-6"/>')}</div>
  </div>
</div>
  `);

  // ===== sequencia =====
  async function run() {
    if (isRunning) return;
    setIsRunning(true);
    if (isAborted()) return;

    if (chatRef.current) chatRef.current.innerHTML = "";
    if (cursorRef.current) cursorRef.current.style.transform = "translate(40px,40px)";

    await showUser(greetUser);
    addTime("10:45");
    await showBotStreamHtml(greetBot);
    addTime("10:45");

    await showUser(qCaso);
    await showBotStreamHtml(botCaso);
    addTime("10:46");

    await showUser(qEmailCliente);
    await showBotStreamHtml(botEmailCliente);
    addTime("10:46");

    await showUser(qEmailSeg);
    await showBotStreamHtml(botEmailSeg);
    addTime("10:47");

    setIsRunning(false);
    completeOnce();
  }

  // contador do composer
  useEffect(() => {
    const handle = () => {
      if (countRef.current && composerRef.current) countRef.current.textContent = `${composerRef.current.value.length}/4000`;
    };
    composerRef.current?.addEventListener("input", handle);
    return () => composerRef.current?.removeEventListener("input", handle);
  }, [token]);

  // Intersection Observer para iniciar animação quando aparece na viewport
  useEffect(() => {
    const chatElement = chatRef.current?.closest('.chat-container');
    if (!chatElement) return;

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isRunning) {
          // Iniciar animação quando entra na viewport
          makeAbort();
          completedRef.current = false;
          setIsRunning(false);
          const hard = setTimeout(() => completeOnce(), 120000);
          const t = setTimeout(run, 400);

          // Cleanup function para o timeout
          const cleanup = () => {
            clearTimeout(t);
            clearTimeout(hard);
          };

          // Armazenar cleanup para poder cancelar se necessário
          (entry.target as any).__cleanup = cleanup;
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.3,
      rootMargin: '0px 0px -100px 0px'
    });

    observer.observe(chatElement);

    return () => {
      observer.disconnect();
      // Cleanup dos timeouts se existirem
      const cleanup = (chatElement as any).__cleanup;
      if (cleanup) cleanup();
    };
  }, [token, loop, isRunning]);

  // (re)inicia ao mudar token/loop - apenas se já estiver visível
  useEffect(() => {
    // Só reinicia se o componente já estiver visível na viewport
    const chatElement = chatRef.current?.closest('.chat-container');
    if (!chatElement) return;

    const rect = chatElement.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (isVisible) {
      makeAbort();
      completedRef.current = false;
      setIsRunning(false);
      const hard = setTimeout(() => completeOnce(), 120000);
      const t = setTimeout(run, 400);
      return () => { clearTimeout(t); clearTimeout(hard); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loop]);

  // abort no unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = composerRef.current;
    if (textarea) {
      const autoResize = () => {
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const minHeight = 80; // Min height from CSS
        const maxHeight = 150; // Max height from CSS
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
        textarea.style.height = newHeight + 'px';
      };

      // Initial resize
      autoResize();

      // Add event listeners for content changes
      textarea.addEventListener('input', autoResize);
      textarea.addEventListener('paste', autoResize);

      // Resize on window resize
      window.addEventListener('resize', autoResize);

      return () => {
        textarea.removeEventListener('input', autoResize);
        textarea.removeEventListener('paste', autoResize);
        window.removeEventListener('resize', autoResize);
      };
    }
  }, []);

  // ===== UI =====
  return (
    <div
      className="chat-animation non-interactive"
      onMouseEnter={() => {
        // console.log('Pausando animação (ChatAnimationAlt)');
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        // console.log('Retomando animação (ChatAnimationAlt)');
        setIsPaused(false);
      }}
      onTouchStart={() => {
        // console.log('Touch detectado - pausando animação (ChatAnimationAlt)');
        setIsPaused(true);
      }}
      onTouchEnd={() => {
        // console.log('Touch terminado - retomando animação (ChatAnimationAlt)');
        // Delay para permitir scroll
        setTimeout(() => {
          setIsPaused(false);
        }, 1000);
      }}
    >
      <div className="wrap">
        <section className="chatApp" style={{ padding: "0px" }}>
          <div className="topbar">
            <div className="crumb">
              <span className="dim">Dashboard</span><span className="sep">›</span><strong>Chat 2</strong>
            </div>
            <div className="head-rt">
              <div className="search">
                <span className="ico">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                  </svg>
                </span>
                <input placeholder="Pesquise" tabIndex={-1} readOnly aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="chat" ref={chatRef} />

          <div className="composer">
            <div className="box">

              <textarea
                ref={composerRef}
                tabIndex={-1}
                readOnly
                placeholder="Escreva a sua mensagem… (Shift+Enter para nova linha)"
                aria-hidden="true"
              />

              <div className="addons" aria-hidden="true">
                <button className="ico-btn" title="Anexar" tabIndex={-1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M21.44 11.05l-8.49 8.49a6 6 0 1 1-8.49-8.49l8.49-8.49a4 4 0 1 1 5.66 5.66l-8.49 8.49a2 2 0 1 1-2.83-2.83L15 6" />
                  </svg>
                </button>
                <button className="ico-btn" title="Microfone" tabIndex={-1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="9" y="4" width="6" height="10" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2" /><path d="M12 19v3" />
                  </svg>
                </button>
                <button className="send" ref={sendBtnRef} aria-label="Enviar" tabIndex={-1}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="hint">
              <span>Prima Enter para enviar • Shift+Enter para nova linha</span>
              <span ref={countRef}>0/4000</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ChatAnimationAlt;
